import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/components/ui/use-toast";
import React from "react";
import {
  getSessionAttendance,
  saveSessionAttendance,
  saveSessionNote,
  submitSessionFeedback,
  type SessionAttendanceData,
  type SessionAttendanceMentee,
} from "@/services/sessionAttendanceService";
import { queryKeys } from "@/services/queryKeyFactory";
import { getDate, formatSessionTime12h } from "@/utils/time-helper";
import { getImgSrc } from "@/utils/file-upload";

const SessionAttendancePage = () => {
  const [searchParams] = useSearchParams();
  const sessionIdParam = searchParams.get("sessionId")?.trim() ?? "";
  const sessionId = sessionIdParam ? parseInt(sessionIdParam, 10) : NaN;
  const validSessionId = Number.isInteger(sessionId) && sessionId > 0 ? sessionId : null;
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error, isError } = useQuery({
    queryKey: [...queryKeys.sessionAttendance, validSessionId],
    queryFn: () => getSessionAttendance(validSessionId!),
    enabled: validSessionId != null,
  });

  const saveMutation = useMutation({
    mutationFn: (attendance: { menteeId: number; attended: boolean }[]) =>
      saveSessionAttendance(validSessionId!, attendance),
    onSuccess: () => {
      if (validSessionId != null) {
        queryClient.invalidateQueries({ queryKey: [...queryKeys.sessionAttendance, validSessionId] });
      }
      toast({ description: t("sessionAttendance.saved") });
    },
    onError: (err: { response?: { data?: { error?: string } }; message?: string }) => {
      const msg = err?.response?.data?.error ?? err?.message ?? t("common.errorDesc");
      toast({ variant: "destructive", title: t("common.errorTitle"), description: msg });
    },
  });

  const saveNoteMutation = useMutation({
    mutationFn: (payload: { note: string; isPrivate: boolean }) =>
      saveSessionNote(validSessionId!, payload),
    onSuccess: () => {
      if (validSessionId != null) {
        queryClient.invalidateQueries({ queryKey: [...queryKeys.sessionAttendance, validSessionId] });
      }
      toast({ description: t("sessionAttendance.noteSaved") });
    },
    onError: (err: { response?: { data?: { error?: string } }; message?: string }) => {
      const msg = err?.response?.data?.error ?? err?.message ?? t("common.errorDesc");
      toast({ variant: "destructive", title: t("common.errorTitle"), description: msg });
    },
  });

  const feedbackMutation = useMutation({
    mutationFn: (payload: { feedback: string; isAnonymous: boolean }) =>
      submitSessionFeedback(validSessionId!, payload),
    onSuccess: () => {
      if (validSessionId != null) {
        queryClient.invalidateQueries({ queryKey: [...queryKeys.sessionAttendance, validSessionId] });
      }
      toast({ description: t("sessionAttendance.feedbackSubmitted") });
    },
    onError: (err: { response?: { data?: { error?: string } }; message?: string }) => {
      const msg = err?.response?.data?.error ?? err?.message ?? t("common.errorDesc");
      toast({ variant: "destructive", title: t("common.errorTitle"), description: msg });
    },
  });

  if (validSessionId == null) {
    return (
      <div className="container max-w-md py-12">
        <Card className="overflow-hidden shadow-lg ring-1 ring-border/50">
          <CardHeader className="border-b bg-muted/30 pb-6">
            <CardTitle className="text-xl">{t("sessionAttendance.invalidLink")}</CardTitle>
            <CardDescription className="mt-2 text-base">{t("sessionAttendance.invalidLinkDesc")}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container max-w-md py-12 flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">{t("common.loading") ?? "Loading…"}</p>
      </div>
    );
  }

  if (isError || !data) {
    const errMsg = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
      ?? (error as Error)?.message
      ?? t("sessionAttendance.loadError");
    return (
      <div className="container max-w-md py-12">
        <Card className="overflow-hidden border-destructive/50 shadow-lg ring-1 ring-destructive/20">
          <CardHeader className="border-b border-destructive/20 bg-destructive/5 pb-6">
            <CardTitle className="text-xl text-destructive">{t("sessionAttendance.error")}</CardTitle>
            <CardDescription className="mt-2 text-base">{errMsg}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (data.isFutureSession) {
    return <FutureSessionView data={data} t={t} />;
  }

  return (
    <SessionAttendanceForm
      data={data}
      onSave={(attendance) => saveMutation.mutate(attendance)}
      onSaveNote={(payload) => saveNoteMutation.mutate(payload)}
      onSubmitFeedback={(payload) => feedbackMutation.mutate(payload)}
      isSaving={saveMutation.isPending}
      isSavingNote={saveNoteMutation.isPending}
      isSubmittingFeedback={feedbackMutation.isPending}
      t={t}
    />
  );
};

type FormProps = {
  data: SessionAttendanceData;
  onSave: (attendance: { menteeId: number; attended: boolean }[]) => void;
  onSaveNote: (payload: { note: string; isPrivate: boolean }) => void;
  onSubmitFeedback: (payload: { feedback: string; isAnonymous: boolean }) => void;
  isSaving: boolean;
  isSavingNote: boolean;
  isSubmittingFeedback: boolean;
  t: (key: string) => string;
};

const SessionAttendanceForm = ({
  data,
  onSave,
  onSaveNote,
  onSubmitFeedback,
  isSaving,
  isSavingNote,
  isSubmittingFeedback,
  t,
}: FormProps) => {
  const [mentees, setMentees] = React.useState<SessionAttendanceMentee[]>(data.mentees);
  const [noteText, setNoteText] = React.useState(data.note?.text ?? "");
  const [noteIsPrivate, setNoteIsPrivate] = React.useState(data.note?.isPrivate ?? true);
  const [feedbackText, setFeedbackText] = React.useState("");
  const [feedbackAnonymous, setFeedbackAnonymous] = React.useState(false);

  React.useEffect(() => {
    setMentees(data.mentees);
  }, [data.mentees]);
  React.useEffect(() => {
    setNoteText(data.note?.text ?? "");
    setNoteIsPrivate(data.note?.isPrivate ?? true);
  }, [data.note?.text, data.note?.isPrivate]);

  const sessionDateFormatted = data.session.sessionDate
    ? getDate(new Date(data.session.sessionDate + "T12:00:00"))
    : data.session.sessionDate;
  const sessionTimeRaw = data.session.sessionTime ?? "";
  const sessionTime = sessionTimeRaw ? formatSessionTime12h(sessionTimeRaw) : "";
  const mentor = data.mentor;
  const isMentorOrAdmin = data.canRecordAttendance || data.canEditNote;
  const [isEditingFeedback, setIsEditingFeedback] = React.useState(false);

  // Initialize feedback form with existing feedback if available
  React.useEffect(() => {
    if (data.myFeedback && !isEditingFeedback) {
      setFeedbackText(data.myFeedback.text);
      setFeedbackAnonymous(data.myFeedback.isAnonymous);
    } else if (!data.myFeedback) {
      setFeedbackText("");
      setFeedbackAnonymous(false);
    }
  }, [data.myFeedback, isEditingFeedback]);

  const toggle = (id: number) => {
    setMentees((prev) =>
      prev.map((m) => (m.id === id ? { ...m, attended: !m.attended } : m))
    );
  };

  const selectAll = (checked: boolean) => {
    setMentees((prev) => prev.map((m) => ({ ...m, attended: checked })));
  };

  const allSelected = mentees.length > 0 && mentees.every((m) => m.attended);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(mentees.map((m) => ({ menteeId: m.id, attended: m.attended })));
  };

  const handleSaveNote = (e: React.FormEvent) => {
    e.preventDefault();
    onSaveNote({ note: noteText, isPrivate: noteIsPrivate });
  };

  const sessionBlock = (titleOverride?: string) => (
    <>
      <CardTitle className="text-xl font-semibold text-text-1">
        {titleOverride ?? t("sessionAttendance.title")}
      </CardTitle>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm font-medium text-text-2">
          <span className="text-text-1">{t("sessionAttendance.sessionDate")}:</span> {sessionDateFormatted}
          {sessionTime ? (
            <>
              {" · "}
              <span className="text-text-1">{t("sessionAttendance.sessionTime")}:</span> {sessionTime}
            </>
          ) : null}
        </p>
      </div>
      {mentor && (
        <div className="flex items-center gap-4 rounded-xl border border-border bg-bg-card p-4 shadow-sm">
          <Avatar className="h-14 w-14 ring-2 ring-background">
            <AvatarImage src={getImgSrc(mentor.profilePictureSrc ?? undefined)} alt={mentor.fullName} />
            <AvatarFallback className="bg-accent/15 text-text-1 text-lg font-semibold">
              {mentor.fullName?.charAt(0) ?? "M"}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="font-semibold leading-tight text-text-1">{mentor.fullName}</p>
            <p className="text-sm text-text-2 truncate">{mentor.email}</p>
          </div>
        </div>
      )}
    </>
  );

  const showFeedbackForm = data.canSubmitFeedback && !data.myFeedbackSubmitted;
  const attendees = mentees.filter((m) => m.attended);

  // Mentee view: show attendee names, notes (if shared), feedback form
  if (!isMentorOrAdmin) {
    return (
      <div className="container max-w-7xl py-12">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_400px]">
          {/* Left Column: Session Details & Attendees */}
          <div className="space-y-6">
            {/* Session Details Card */}
            <Card className="overflow-hidden border border-border bg-bg-card shadow-sm">
              <CardHeader className="space-y-4 border-b border-border bg-bg-elevated/60 pb-6">
                {sessionBlock(t("sessionAttendance.menteeTitle"))}
              </CardHeader>
              <CardContent className="pt-6">
                {/* Attendees list */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-text-1">{t("sessionAttendance.attendees")}</h3>
                  {attendees.length > 0 ? (
                    <ul className="space-y-2">
                      {attendees.map((m) => (
                        <li
                          key={m.id}
                          className="flex items-center gap-3 rounded-lg border border-border bg-bg-card px-4 py-3"
                        >
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={getImgSrc(m.profilePictureSrc ?? undefined)} alt={m.fullName} />
                            <AvatarFallback className="bg-accent/15 text-text-1 text-sm font-semibold">
                              {m.fullName
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .slice(0, 2)
                                .toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium text-text-1">{m.fullName}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-text-2">{t("sessionAttendance.noAttendeesYet")}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Session Notes & Feedback */}
          <div className="space-y-6">
            {/* Session notes (only if shared) */}
            {data.note?.text && (
              <Card className="overflow-hidden border border-border bg-bg-card shadow-sm">
                <CardHeader className="border-b border-border bg-bg-elevated/60 pb-6">
                  <CardTitle className="text-lg font-semibold text-text-1">{t("sessionAttendance.sessionNotes")}</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="rounded-lg border border-border bg-bg-elevated/60 p-4">
                    <p className="whitespace-pre-wrap text-sm text-text-1">{data.note.text}</p>
                    {data.note.updatedAt && (
                      <p className="mt-2 text-xs text-text-3">
                        {t("sessionAttendance.lastUpdated")}{" "}
                        {getDate(new Date(data.note.updatedAt))}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Feedback section */}
            <Card className="overflow-hidden border border-border bg-bg-card shadow-sm">
              <CardHeader className="border-b border-border bg-bg-elevated/60 pb-6">
                <CardTitle className="text-lg font-semibold text-text-1">{t("sessionAttendance.leaveFeedback")}</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Show existing feedback if submitted and not editing */}
                {data.myFeedback && !isEditingFeedback && (
                  <div className="space-y-4">
                    <div className="rounded-lg border border-border bg-bg-elevated/60 p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-sm text-text-1 whitespace-pre-wrap">{data.myFeedback.text}</p>
                          {data.myFeedback.submittedAt && (
                            <p className="mt-2 text-xs text-text-3">
                              {t("sessionAttendance.submittedOn")}{" "}
                              {getDate(new Date(data.myFeedback.submittedAt))}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="lg"
                        className="font-semibold"
                        onClick={() => setIsEditingFeedback(true)}
                      >
                        {t("common.edit")}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Feedback form (for new submission or editing) */}
                {(showFeedbackForm || (data.myFeedbackSubmitted && isEditingFeedback)) && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      onSubmitFeedback({ feedback: feedbackText, isAnonymous: feedbackAnonymous });
                      setIsEditingFeedback(false);
                    }}
                    className="space-y-4"
                  >
                    <textarea
                      className="min-h-[120px] w-full rounded-md border border-border bg-bg-card px-3 py-2 text-sm text-text-1 placeholder:text-text-3 ring-offset-bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:border-accent/50"
                      placeholder={t("sessionAttendance.feedbackPlaceholder")}
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      required
                    />
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={feedbackAnonymous}
                        onChange={(e) => setFeedbackAnonymous(e.target.checked)}
                        className="h-4 w-4 rounded border-border"
                      />
                      <span className="text-sm text-text-1">{t("sessionAttendance.feedbackAnonymous")}</span>
                    </label>
                    <div className="flex justify-end gap-2 pt-2">
                      {isEditingFeedback && (
                        <Button
                          type="button"
                          variant="outline"
                          size="lg"
                          className="font-semibold"
                          onClick={() => {
                            setIsEditingFeedback(false);
                            if (data.myFeedback) {
                              setFeedbackText(data.myFeedback.text);
                              setFeedbackAnonymous(data.myFeedback.isAnonymous);
                            }
                          }}
                        >
                          {t("common.cancel")}
                        </Button>
                      )}
                      <Button
                        type="submit"
                        size="lg"
                        className="min-w-[120px] font-semibold"
                        disabled={isSubmittingFeedback || !feedbackText.trim()}
                      >
                        {isSubmittingFeedback && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {isEditingFeedback ? t("common.save") : t("sessionAttendance.submitFeedback")}
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Mentor/Admin view: record attendance, edit notes, view feedback
  return (
    <div className="container max-w-4xl py-12">
      <div className="grid gap-8 md:grid-cols-2">
        {/* Attendance recording card */}
        <Card className="overflow-hidden border border-border bg-bg-card shadow-sm">
          <CardHeader className="space-y-4 border-b border-border bg-bg-elevated/60 pb-6">
            {sessionBlock()}
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {mentees.length === 0 ? (
                <p className="rounded-lg bg-bg-elevated/60 px-4 py-6 text-center text-text-2">
                  {t("sessionAttendance.noMentees")}
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="font-medium"
                      onClick={() => selectAll(!allSelected)}
                    >
                      {allSelected ? t("sessionAttendance.deselectAll") : t("sessionAttendance.selectAll")}
                    </Button>
                  </div>
                  <ul className="space-y-2">
                    {mentees.map((m) => (
                      <li
                        key={m.id}
                        className="flex items-center gap-4 rounded-lg border border-border bg-bg-card px-4 py-3 transition-colors hover:bg-bg-elevated/50 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent/40"
                      >
                        <Checkbox
                          id={`mentee-${m.id}`}
                          checked={m.attended}
                          onCheckedChange={() => toggle(m.id)}
                          className="border-2 border-border"
                        />
                        <label
                          htmlFor={`mentee-${m.id}`}
                          className="flex-1 cursor-pointer font-medium leading-tight text-text-1"
                        >
                          {m.fullName}
                        </label>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  size="lg"
                  className="min-w-[120px] font-semibold"
                  disabled={isSaving || mentees.length === 0}
                >
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t("common.save")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Session notes card (mentor/admin can edit) */}
        {data.canEditNote && (
          <Card className="overflow-hidden border border-border bg-bg-card shadow-sm">
            <CardHeader className="border-b border-border bg-bg-elevated/60 pb-6">
              <CardTitle className="text-xl font-semibold text-text-1">
                {t("sessionAttendance.sessionNotes")}
              </CardTitle>
              <CardDescription className="mt-1 text-text-2">
                {t("sessionAttendance.sessionNotesDesc")}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSaveNote} className="space-y-4">
                <textarea
                  className="min-h-[120px] w-full rounded-md border border-border bg-bg-card px-3 py-2 text-sm text-text-1 placeholder:text-text-3 ring-offset-bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:border-accent/50"
                  placeholder={t("sessionAttendance.notePlaceholder")}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                />
                <div className="space-y-2">
                  <p className="text-sm font-medium text-text-1">{t("sessionAttendance.noteVisibility")}</p>
                  <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="radio"
                        name="noteVisibility"
                        checked={noteIsPrivate}
                        onChange={() => setNoteIsPrivate(true)}
                        className="h-4 w-4"
                      />
                      <span className="text-sm text-text-1">{t("sessionAttendance.notePrivate")}</span>
                    </label>
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="radio"
                        name="noteVisibility"
                        checked={!noteIsPrivate}
                        onChange={() => setNoteIsPrivate(false)}
                        className="h-4 w-4"
                      />
                      <span className="text-sm text-text-1">{t("sessionAttendance.noteVisibleToGroup")}</span>
                    </label>
                  </div>
                </div>
                {data.note?.updatedAt && (
                  <p className="text-xs text-text-3">
                    {t("sessionAttendance.lastUpdated")} {getDate(new Date(data.note.updatedAt))}
                  </p>
                )}
                <div className="flex justify-end pt-2">
                  <Button type="submit" size="lg" className="min-w-[120px] font-semibold" disabled={isSavingNote}>
                    {isSavingNote && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t("sessionAttendance.saveNote")}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Feedback list (read-only for mentor/admin) */}
      {(data.feedbackList?.length ?? 0) > 0 && (
        <Card className="mt-8 overflow-hidden border border-border bg-bg-card shadow-sm">
          <CardHeader className="border-b border-border bg-bg-elevated/60 pb-6">
            <CardTitle className="text-xl font-semibold text-text-1">
              {t("sessionAttendance.feedbackTitle")}
            </CardTitle>
            <CardDescription className="mt-1 text-text-2">
              {t("sessionAttendance.feedbackTitleDesc")}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <ul className="space-y-3">
              {data.feedbackList!.map((item, idx) => (
                <li key={idx} className="rounded-lg border border-border bg-bg-elevated/60 p-4">
                  <p className="text-xs font-medium text-text-2">{item.displayName}</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-text-1">{item.text}</p>
                  {item.submittedAt && (
                    <p className="mt-2 text-xs text-text-3">
                      {getDate(new Date(item.submittedAt))}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ---------- Future Session: read-only view ---------- */

type FutureSessionViewProps = {
  data: SessionAttendanceData;
  t: (key: string) => string;
};

const FutureSessionView = ({ data, t }: FutureSessionViewProps) => {
  const sessionDateFormatted = data.session.sessionDate
    ? getDate(new Date(data.session.sessionDate + "T12:00:00"))
    : data.session.sessionDate;
  const sessionTimeRaw = data.session.sessionTime ?? "";
  const sessionTime = sessionTimeRaw ? formatSessionTime12h(sessionTimeRaw) : "";
  const mentor = data.mentor;

  return (
    <div className="container max-w-md py-12">
      <Card className="overflow-hidden border border-border bg-bg-card shadow-sm">
        <CardHeader className="space-y-4 border-b border-border bg-bg-elevated/60 pb-6">
          <CardTitle className="text-xl font-semibold text-text-1">
            {t("sessionAttendance.upcomingTitle")}
          </CardTitle>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm font-medium text-text-2">
              <span className="text-text-1">{t("sessionAttendance.sessionDate")}:</span> {sessionDateFormatted}
              {sessionTime ? (
                <>
                  {" · "}
                  <span className="text-text-1">{t("sessionAttendance.sessionTime")}:</span> {sessionTime}
                </>
              ) : null}
            </p>
          </div>
          {mentor && (
            <div className="flex items-center gap-4 rounded-xl border border-border bg-bg-card p-4 shadow-sm">
              <Avatar className="h-14 w-14 ring-2 ring-background">
                <AvatarImage src={getImgSrc(mentor.profilePictureSrc ?? undefined)} alt={mentor.fullName} />
                <AvatarFallback className="bg-accent/15 text-text-1 text-lg font-semibold">
                  {mentor.fullName?.charAt(0) ?? "M"}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="font-semibold leading-tight text-text-1">{mentor.fullName}</p>
                <p className="text-sm text-text-2 truncate">{mentor.email}</p>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="rounded-lg bg-bg-elevated/60 px-4 py-3 text-sm text-text-2">
            {t("sessionAttendance.upcomingDesc")}
          </div>

          {/* Group members */}
          {data.mentees.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-text-1">{t("sessionAttendance.groupMembers")}</h3>
              <ul className="space-y-1">
                {data.mentees.map((m) => (
                  <li key={m.id} className="flex items-center gap-3 rounded-lg border border-border bg-bg-card px-3 py-2">
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={getImgSrc(m.profilePictureSrc ?? undefined)} alt={m.fullName} />
                      <AvatarFallback className="bg-accent/15 text-text-1 text-xs font-semibold">
                        {m.fullName.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium text-text-1">{m.fullName}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Session note (if exists and visible) */}
          {data.note?.text && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-text-1">{t("sessionAttendance.sessionNotes")}</h3>
              <div className="rounded-lg border border-border bg-bg-elevated/60 p-4">
                <p className="whitespace-pre-wrap text-sm text-text-1">{data.note.text}</p>
                {data.note.updatedAt && (
                  <p className="mt-2 text-xs text-text-3">
                    {t("sessionAttendance.lastUpdated")} {getDate(new Date(data.note.updatedAt))}
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SessionAttendancePage;
